import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  archivePersonaDomainTemplate,
  createPersonaDomainTemplate,
  getPersonaDomainTemplate,
  getPersonaDomainTemplates,
  updatePersonaDomainTemplate,
} from '../../services/api';
import './EnterpriseListPage.scss';
import './DomainTemplatesPage.scss';

const emptyRow = () => ({ key: '', label: '', hints: '' });

const BASE_PRESETS = [
  { id: 'finance', label: '금융형 (flat DNA, 권장)' },
  { id: 'telecom', label: '통신형 (점수/차원 훅)' },
];

function DomainTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const [formId, setFormId] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formBase, setFormBase] = useState('finance');
  const [formDescription, setFormDescription] = useState('');
  const [rows, setRows] = useState([emptyRow()]);

  const customTemplates = useMemo(
    () => (templates || []).filter((t) => !t.builtin && t.status !== 'archived'),
    [templates]
  );
  const builtinTemplates = useMemo(
    () => (templates || []).filter((t) => t.builtin),
    [templates]
  );

  const selected = useMemo(
    () => (templates || []).find((t) => t.id === selectedId) || null,
    [templates, selectedId]
  );

  const resetCreateForm = () => {
    setIsCreating(true);
    setSelectedId(null);
    setFormId('');
    setFormLabel('');
    setFormBase('finance');
    setFormDescription('');
    setRows([emptyRow()]);
    setError(null);
    setNotice(null);
  };

  const loadTemplateIntoForm = async (template) => {
    setIsCreating(false);
    setSelectedId(template.id);
    setError(null);
    setNotice(null);
    try {
      const detail = await getPersonaDomainTemplate(template.id);
      setFormId(detail.id);
      setFormLabel(detail.label || '');
      setFormBase(detail.base_preset_id || detail.basePresetId || detail.preset_id || 'finance');
      setFormDescription(detail.description || '');
      const vars = Array.isArray(detail.variables) ? detail.variables : [];
      setRows(
        vars.length
          ? vars.map((v) => ({ key: v.key || '', label: v.label || '', hints: v.hints || '' }))
          : [emptyRow()]
      );
    } catch (err) {
      console.error(err);
      setError(err.message || '템플릿 상세를 불러오지 못했습니다.');
    }
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPersonaDomainTemplates();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error(err);
      setError(err.message || '템플릿 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSave = async () => {
    const variables = rows
      .map((row) => ({
        key: row.key.trim(),
        label: row.label.trim(),
        hints: row.hints.trim(),
      }))
      .filter((row) => row.key);

    if (!variables.length) {
      setError('변수를 최소 1개 입력하세요.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      if (isCreating) {
        const data = await createPersonaDomainTemplate({
          id: formId.trim().toLowerCase(),
          label: formLabel.trim(),
          basePresetId: formBase,
          description: formDescription.trim() || undefined,
          variables,
        });
        setNotice(`템플릿 "${data.label || data.id}"을 만들었습니다.`);
        await fetchTemplates();
        setIsCreating(false);
        setSelectedId(data.id);
      } else {
        if (selected?.builtin) {
          setError('내장 템플릿은 수정할 수 없습니다. 새 템플릿을 만드세요.');
          return;
        }
        const data = await updatePersonaDomainTemplate(formId, {
          label: formLabel.trim(),
          basePresetId: formBase,
          description: formDescription.trim() || undefined,
          variables,
        });
        setNotice(`템플릿 "${data.label || data.id}"을 저장했습니다.`);
        await fetchTemplates();
      }
    } catch (err) {
      console.error(err);
      setError(err.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedId || selected?.builtin) return;
    if (!window.confirm(`템플릿 "${selectedId}"을 보관(삭제)할까요?`)) return;
    try {
      setSaving(true);
      setError(null);
      await archivePersonaDomainTemplate(selectedId);
      setNotice('템플릿을 보관 처리했습니다.');
      resetCreateForm();
      setIsCreating(false);
      await fetchTemplates();
    } catch (err) {
      console.error(err);
      setError(err.message || '보관 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const editingBuiltin = !isCreating && selected?.builtin;

  return (
    <div className="domain-templates-page enterprise-list-page">
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <h1 className="sa-page-title">도메인 템플릿</h1>
          <p className="sa-page-subtitle">
            교통·금융2 같은 새 DNA 변수 템플릿을 만들고, 회사 스키마에서 선택해 쓸 수 있습니다.
          </p>
        </div>
        <button type="button" className="sa-btn sa-btn-primary" onClick={resetCreateForm}>
          <FontAwesomeIcon icon={faPlus} /> 새 템플릿
        </button>
      </div>

      {(error || notice) && (
        <div className={`domain-banner ${error ? 'error' : 'ok'}`}>{error || notice}</div>
      )}

      <div className="domain-workspace">
        <aside className="domain-list-panel">
          <div className="panel-title">내장 템플릿</div>
          {builtinTemplates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              className={`domain-row ${selectedId === tpl.id && !isCreating ? 'active' : ''}`}
              onClick={() => loadTemplateIntoForm(tpl)}
            >
              <div className="name">{tpl.label}</div>
              <div className="meta">
                <span>builtin</span>
                <span>{tpl.variable_count ?? tpl.variableCount ?? 0} vars</span>
              </div>
            </button>
          ))}

          <div className="panel-title" style={{ marginTop: 16 }}>커스텀 템플릿</div>
          {loading && <div className="empty">불러오는 중…</div>}
          {!loading && customTemplates.length === 0 && (
            <div className="empty">아직 없습니다. 새 템플릿을 만들어 보세요.</div>
          )}
          {customTemplates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              className={`domain-row ${selectedId === tpl.id && !isCreating ? 'active' : ''}`}
              onClick={() => loadTemplateIntoForm(tpl)}
            >
              <div className="name">{tpl.label}</div>
              <div className="meta">
                <span>{tpl.id}</span>
                <span>{tpl.variable_count ?? tpl.variableCount ?? 0} vars</span>
              </div>
            </button>
          ))}
        </aside>

        <section className="domain-editor">
          {!isCreating && !selectedId ? (
            <div className="empty-main">왼쪽에서 템플릿을 고르거나 새 템플릿을 만드세요.</div>
          ) : (
            <>
              <div className="editor-header">
                <h2>{isCreating ? '새 템플릿 만들기' : formLabel || formId}</h2>
                {editingBuiltin && (
                  <span className="readonly-pill">읽기 전용 · 복제하려면 새 템플릿으로 만드세요</span>
                )}
              </div>

              <div className="editor-fields">
                <div className="field-group">
                  <label htmlFor="tpl-id">템플릿 id</label>
                  <input
                    id="tpl-id"
                    value={formId}
                    onChange={(e) => setFormId(e.target.value.toLowerCase())}
                    placeholder="예: transport, finance_v2"
                    disabled={!isCreating || saving}
                  />
                  <span className="hint">영문 소문자 시작, 소문자/숫자/_ 만</span>
                </div>
                <div className="field-group">
                  <label htmlFor="tpl-label">표시명</label>
                  <input
                    id="tpl-label"
                    value={formLabel}
                    onChange={(e) => setFormLabel(e.target.value)}
                    placeholder="예: 교통"
                    disabled={editingBuiltin || saving}
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="tpl-base">생성 훅 기반</label>
                  <select
                    id="tpl-base"
                    value={formBase}
                    onChange={(e) => setFormBase(e.target.value)}
                    disabled={editingBuiltin || saving}
                  >
                    {BASE_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-group" style={{ marginBottom: 14 }}>
                <label htmlFor="tpl-desc">설명 (선택)</label>
                <input
                  id="tpl-desc"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="이 템플릿이 다루는 도메인 설명"
                  disabled={editingBuiltin || saving}
                />
              </div>

              <table className="sa-table">
                <thead>
                  <tr>
                    <th style={{ width: '24%' }}>변수 key</th>
                    <th style={{ width: '26%' }}>라벨</th>
                    <th>검색 힌트</th>
                    <th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={`r-${index}`}>
                      <td>
                        <input
                          className="cell-input"
                          value={row.key}
                          onChange={(e) => updateRow(index, 'key', e.target.value)}
                          disabled={editingBuiltin || saving}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input"
                          value={row.label}
                          onChange={(e) => updateRow(index, 'label', e.target.value)}
                          disabled={editingBuiltin || saving}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input"
                          value={row.hints}
                          onChange={(e) => updateRow(index, 'hints', e.target.value)}
                          disabled={editingBuiltin || saving}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="icon-btn"
                          disabled={editingBuiltin || saving || rows.length <= 1}
                          onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="editor-footer">
                <button
                  type="button"
                  className="sa-btn sa-btn-secondary"
                  disabled={editingBuiltin || saving}
                  onClick={() => setRows((prev) => [...prev, emptyRow()])}
                >
                  <FontAwesomeIcon icon={faPlus} /> 변수 추가
                </button>
                <div className="footer-actions">
                  {!isCreating && !editingBuiltin && (
                    <button
                      type="button"
                      className="sa-btn sa-btn-secondary"
                      onClick={handleArchive}
                      disabled={saving}
                    >
                      보관
                    </button>
                  )}
                  <button
                    type="button"
                    className="sa-btn sa-btn-primary"
                    onClick={handleSave}
                    disabled={editingBuiltin || saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? '저장 중…' : '저장'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default DomainTemplatesPage;
